import i18next from "i18next";
import { AuthStore } from "src/stores/AuthStore";
import { MessageType } from "src/components/notifications/Notifier";
import { ConfigSettings } from "./NetworkConfig";
import { User } from "./User";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GetUserRating, GetUserRatingVariables } from "src/api/generated/GetUserRating";
import { GET_USER_RATING } from "src/api/userRating";
import { v4 as uuidv4 } from "uuid";
import { setCookie } from "src/utils/Cookie";
import { Route } from "src/config/routes";
import * as Sentry from "@sentry/react";
import Axios from "./Axios";

function info(message: string) {
    // console.log(message);
}

export class AuthError extends Error {
    area: string;
    status?: number;
    constructor(message: string, area: string, status?: number) {
        super(message);
        this.name = "AuthError";
        this.area = area;
        this.status = status;
    }
}

export function removeLoginCodeParam(urlOrPath: string): string {
    if (!urlOrPath) {
        return urlOrPath;
    }

    const isAbsolute = /^https?:\/\//.test(urlOrPath);
    const url = new URL(urlOrPath, window.location.origin);
    url.searchParams.delete("code");

    return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}

export function isPermanentRefreshError(error: unknown): boolean {
    return error instanceof AuthError && error.status !== undefined && error.status >= 400 && error.status < 500;
}

export class AuthClient {
    private config: ConfigSettings;
    private authStore: AuthStore;

    constructor(config: ConfigSettings, authStore: AuthStore) {
        this.config = config;
        this.authStore = authStore;
    }

    async initiateLogin() {
        const loginCodeVerifier = uuidv4();
        this.authStore.setLoginCodeVerifier(loginCodeVerifier);
        const encoder = new TextEncoder();
        const data = encoder.encode(loginCodeVerifier);
        const hash = await crypto.subtle.digest("SHA-256", data);
        const hashArray = new Uint8Array(hash);
        // Convert to base64 without using Buffer or base64url
        const loginCodeChallenge = btoa(String.fromCharCode(...hashArray))
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=/g, "");

        const isRegisterRoute = window.location.pathname === Route.register;
        const hasRegistrationCode = window.location.search.includes("code");

        // `includes("localhost")` would also match `*.wwportal.localhost`
        // (the local-stage gateway domain), routing dev users to a bogus
        // http://localhost:3000 redirect_uri. Pin to a true "localhost" or
        // "localhost:<port>" host so only bare-localhost dev gets the alias.
        const isLocalhostHost = /^localhost(:\d+)?$/.test(window.location.host);

        let redirectUri = isLocalhostHost
            ? `http://localhost:3000${removeLoginCodeParam(this.authStore.entryPath)}`
            : removeLoginCodeParam(this.authStore.entryUri);

        // register route does not exist in portal, solely used for redirection
        if (isRegisterRoute) {
            redirectUri = redirectUri.split(Route.register)[0];
        }

        this.authStore.setLoginRedirectUri(redirectUri);

        const subdomain = isLocalhostHost
            ? this.config.environment
            : window.location.host.split(".")[0];

        let urlInitiateLoginEndpoint = `${this.config.cidaasBaseUrl}${this.config.cidaasAuthorisationServerRessource}?client_id=${this.config.wwPortalClientId}&response_type=code&scopes=${this.authStore.scopes}&redirect_uri=${redirectUri}&code_challenge=${loginCodeChallenge}&code_challenge_method=${this.authStore.loginCodeChallengeMethod}&subdomain=${subdomain}&isCidaasRedirect=true`;

        // register route does not exist in portal anymore, solely used for redirection
        if (isRegisterRoute) {
            if (hasRegistrationCode) {
                const registrationCodeParam = new URLSearchParams(window.location.search).get("code");
                urlInitiateLoginEndpoint = `${urlInitiateLoginEndpoint}&registrationcode=${registrationCodeParam}`;
            } else {
                urlInitiateLoginEndpoint = `${urlInitiateLoginEndpoint}&registrationcode=empty`;
            }
        }

        // Redirect to cidaas for fresh requestId which afterward redirects to auth.wwportal.ch including the subdomain
        window.location.href = urlInitiateLoginEndpoint;
    }

    async login(loginVerificationCode: string): Promise<boolean> {
        info("loginCidaas starts");
        this.authStore.setLogin(true);

        const getCidaasAccessTokenRequestBody = {
            grant_type: "authorization_code",
            client_id: this.config.wwPortalClientId,
            code: loginVerificationCode,
            code_verifier: this.authStore.loginCodeVerifier,
            scope: this.authStore.scopes,
            redirect_uri: this.authStore.loginRedirectUri
        };

        try {
            const { data } = await Axios.getInstance().post(
                `${this.config.cidaasBaseUrl}${this.config.cidaasGetTokenRessource}`,
                new URLSearchParams(getCidaasAccessTokenRequestBody) // Should be automatically serialized to application/x-www-form-urlencoded
            );

            const user: User = new User();
            await user.init(data.access_token, data.sub, data.identity_id);

            this.authStore.addUser(user, data.access_token, data.refresh_token, data.expires_in);

            setCookie("token", data.access_token, "Strict");

            const result = await apolloClientInstance.query<GetUserRating, GetUserRatingVariables>({
                query: GET_USER_RATING,
                variables: {
                    userid: user.userid
                }
            });

            if (!result.error && result.data.ums_users[0]?.id) {
                this.authStore.rootStore.userRatingModalStore.setRatingState(
                    result.data.ums_users[0].ratingstateportal
                );
                this.authStore.rootStore.userRatingModalStore.setRatingStateAssemblies(
                    result.data.ums_users[0].ratingstateassemblies
                );
            }

            info("loginCidaas successful");
            this.authStore.setLogin(false);
            return Promise.resolve(true);
        } catch (error) {
            info(`loginCidaas unsuccessful ${error}`);
            this.authStore.setLogin(false);
            return Promise.reject(new AuthError(`${error}`, "login"));
        }
    }

    async relogin(refreshToken: string | undefined): Promise<boolean> {
        if (refreshToken) {
            if (!this.authStore.relogin) {
                info("relogin start");
                this.authStore.setRelogin(true);
                try {
                    const success = await this.authStore.refreshTokens();
                    return Promise.resolve(success);
                } catch (error) {
                    info(`relogin unsuccessful: ${error}`);
                    return Promise.reject(new AuthError(`relogin unsuccessful: ${error}`, "relogin"));
                } finally {
                    this.authStore.setRelogin(false);
                }
            }
            info("relogin already in progress");
            return Promise.resolve(false);
        }
        info("relogin omitted because no token available");
        return Promise.resolve(false);
    }

    async refresh(refreshToken: string | undefined, isRetry: boolean = false): Promise<boolean> {
        // TODO: Cookie hat das refresh-token
        if (refreshToken) {
            try {
                const { data } = await Axios.getInstance().post(
                    `${this.config.cidaasBaseUrl}${this.config.cidaasGetTokenRessource}`,
                    new URLSearchParams({
                        grant_type: "refresh_token",
                        refresh_token: refreshToken,
                        response_type: this.authStore.refreshTokenResponseType,
                        client_id: this.config.wwPortalClientId,
                        scope: this.authStore.scopes
                    }) // Should be automatically serialized to application/x-www-form-urlencoded
                );

                const user: User = new User();
                if (this.authStore.user) {
                    this.authStore.addUser(this.authStore.user, data.access_token, data.refresh_token, data.expires_in);
                } else {
                    await user.init(data.access_token, data.sub, data.identity_id);
                    this.authStore.addUser(user, data.access_token, data.refresh_token, data.expires_in);
                }

                setCookie("token", data.access_token, "Strict");

                return Promise.resolve(true);
            } catch (error: any) {
                Sentry.captureException(error, {
                    level: "error",
                    user: {
                        id: this.authStore.user?.userid,
                        customerid: this.authStore.user?.customerid,
                        cidaasUserId: this.authStore.user?.userIdCidaas,
                        cidaasIdentityId: this.authStore.user?.identityIdCidaas,
                        email: this.authStore.user?.username,
                        username: this.authStore.user?.username
                    },
                    fingerprint: ["TokenRefresh"],
                    tags: {
                        exceptionName: "TokenRefresh",
                        retried: isRetry
                    },
                    extra: {
                        exceptionName: "TokenRefresh",
                        cidaas_grant_type: "refresh_token",
                        cidaas_refresh_token: refreshToken,
                        cidaas_response_type: this.authStore.refreshTokenResponseType,
                        cidaas_client_id: this.config.wwPortalClientId,
                        cidaas_scope: this.authStore.scopes
                    }
                });
                console.log(JSON.stringify(error, undefined, 4));
                console.log(`"token refresh not successful --> ${error}`);

                return Promise.reject(
                    new AuthError(`token refresh not successful --> ${error}`, "refresh", error.response?.status)
                );
            }
        }
        info("refresh omitted because no refresh-token available");
        return Promise.resolve(false);
    }

    async logout(): Promise<boolean> {
        info("logout starts");

        this.authStore.setLogout(true);

        if (this.authStore.user || this.authStore.token) {
            const username = this.authStore.user?.username;
            if (this.authStore.token) {
                try {
                    await Axios.getInstance().post(
                        `${this.config.cidaasBaseUrl}${this.config.cidaasEndSessionRessource}?access_token_hint=${this.authStore.token}`,
                        undefined,
                        {
                            urlWithoutParams: `${this.config.cidaasBaseUrl}${this.config.cidaasEndSessionRessource}`
                        }
                    );
                    info(`logout token revocation successful: ${username}`);
                } catch (error) {
                    info(`logout token revocation not successful: ${username}`);
                    info(`--> ${error}`);
                }
            } else {
                info("revocation omitted because no token available");
            }
            this.authStore.removeUser();
            info(`logout successful: ${username}`);
            this.authStore.setLogout(false);
            return Promise.resolve(true);
        } else {
            info("logout omitted because no user or token available");
            this.authStore.setLogout(false);
            return Promise.resolve(false);
        }
    }

    async passwordChange(password: string, newpassword: string): Promise<boolean> {
        info("password change starts");

        const changePasswordBody = {
            client_id: this.config.wwPortalClientId,
            redirect_url: window.location.origin,
            identityId: this.authStore.user?.identityIdCidaas,
            sub: this.authStore.user?.userIdCidaas,
            old_password: password,
            new_password: newpassword,
            confirm_password: newpassword,
            notify_user: false,
            need_reset_password_on_next_login: false
        };

        try {
            await Axios.getAuthInstance().post(
                `${this.config.cidaasBaseUrl}${this.config.cidaasChangePasswordRessource}`,
                changePasswordBody
            );
            info("password change successful");
            return Promise.resolve(true);
        } catch (error) {
            info("password change not successful");
            info(`--> ${error}`);
            return Promise.reject(
                new AuthError(`Beim Ändern des Passworts ist ein Fehler aufgetreten: ${error}`, "password change")
            );
        }
    }

    getLanguageCheck(language: string) {
        if (language !== "de" && language !== "en" && language !== "fr" && language !== "it") {
            language = "en";
        }
        return language;
    }

    async passwordRecovery(username: string): Promise<boolean> {
        try {
            info(`password recovery starts: ${username}`);
            await Axios.getInstance().post(`${this.config.authority}/account/forgotpasswordapp`, {
                email: username,
                language: this.getLanguageCheck(i18next.language.split("-")[0])
            });
            info(`password recovery successful: ${username}`);
            return Promise.resolve(true);
        } catch (error) {
            info(`password recovery not successful: ${username}`);
            info(`--> ${error}`);
            return Promise.reject(new AuthError(`${error}`, "password recovery"));
        }
    }

    async resendActivationEmail(username: string): Promise<boolean> {
        try {
            info(`re-send activation email starts: ${username}`);
            await Axios.getInstance().post(`${this.config.authority}/account/sendactivationemail`, {
                username: username,
                language: this.getLanguageCheck(i18next.language.split("-")[0])
            });
            info(`re-send activation email successful: ${username}`);
            return Promise.resolve(true);
        } catch (error) {
            info(`re-send activation email not successful: ${username}`);
            info(`--> ${error}`);
            return Promise.reject(new AuthError(`${error}`, "re-send activation email"));
        }
    }

    async passwordReset(username: string, code: string, newpassword: string): Promise<boolean> {
        try {
            info(`password reset starts: ${username}`);
            await Axios.getInstance().post(`${this.config.authority}/account/passwordresetapp`, {
                username: username,
                code: code,
                newpassword: newpassword
            });
            info(`password reset successful: ${username}`);
            return Promise.resolve(true);
        } catch (error) {
            info(`password reset not successful: ${username}`);
            info(`--> ${error}`);
            return Promise.reject(new AuthError(`${error}`, "password reset"));
        }
    }

    async register(username: string, password: string, registrationcode: string): Promise<boolean> {
        try {
            info(`register starts: ${username}`);
            Axios.getInstance().post(`${this.config.authority}/account/registeruserapp`, {
                username: username,
                password: password,
                passwordconfirm: password,
                registrationcode: registrationcode,
                language: this.getLanguageCheck(i18next.language.split("-")[0])
            });
            info(`register successful: ${username}`);
            return Promise.resolve(true);
        } catch (error) {
            info(`register not successful: ${username}`);
            info(`--> ${error}`);
            return Promise.reject(new AuthError(`${error}`, "register"));
        }
    }

    async activate(username: string, code: string): Promise<boolean> {
        try {
            info(`activation starts: ${username}`);
            await Axios.getInstance().post(`${this.config.authority}/account/activateuserapp`, {
                username: username,
                code: code
            });
            info(`activation successful: ${username}`);
            return Promise.resolve(true);
        } catch (error) {
            info(`activation not successful: ${username}`);
            info(`--> ${error}`);
            return Promise.reject(new AuthError(`${error}`, "activation"));
        }
    }

    async emailChange(newEmail: string): Promise<boolean> {
        info("email change starts");

        const setNewEmailBody = {
            registrationcode: this.authStore.user?.userIdCidaas,
            email: newEmail
        };

        try {
            await Axios.getAuthInstance().post(`${this.config.setNewEmail}`, setNewEmailBody);
            info("email change successful");
            return Promise.resolve(true);
        } catch (error) {
            info("email change not successful");
            info(`--> ${error}`);
            return Promise.reject(
                new AuthError(`Beim Ändern des Emails ist ein Fehler aufgetreten: ${error}`, "email change")
            );
        }
    }

    async toggleMfa(mfaEnabled: boolean, userId?: string): Promise<boolean> {
        info("mfa toggle starts");

        // The backend matches the body property name exactly: "userId" (a lowercase "userid" is ignored and
        // would toggle the caller's own MFA instead of the target user's).
        const setMfaBody = {
            mfaenabled: mfaEnabled,
            ...(userId ? { userId } : {})
        };

        try {
            await Axios.getAuthInstance().post(`${this.config.setMfa}`, setMfaBody);
            info("mfa toggle successful");

            this.authStore.rootStore.uiStore.printStatusMessage(
                i18next.t(mfaEnabled ? "screens.auth.mfa.enable_success" : "screens.auth.mfa.disable_success"),
                MessageType.SUCCESS
            );

            return Promise.resolve(true);
        } catch (error) {
            info("mfa toggle not successful");
            info(`--> ${error}`);

            this.authStore.rootStore.uiStore.printStatusMessage(
                i18next.t(mfaEnabled ? "screens.auth.mfa.enable_error" : "screens.auth.mfa.disable_error"),
                MessageType.ERROR
            );

            return Promise.resolve(false);
        }
    }

    /**
     * Admin only: sets whether MFA is mandatory for one user (`mandatory: true` is stored as
     * MFA.HASPERMISSION = 'false' by the backend). Shows the success or error message itself.
     */
    async setMfaMandatory(mandatory: boolean, userId: string): Promise<boolean> {
        info("mfa mandatory starts");

        try {
            await Axios.getAuthInstance().post(`${this.config.setMfaMandatory}`, { mandatory, userId });
            info("mfa mandatory successful");

            this.authStore.rootStore.uiStore.printStatusMessage(
                i18next.t(
                    mandatory
                        ? "screens.customer_users.mfa_mandatory_enable_success"
                        : "screens.customer_users.mfa_mandatory_disable_success"
                ),
                MessageType.SUCCESS
            );

            return Promise.resolve(true);
        } catch (error) {
            info("mfa mandatory not successful");
            info(`--> ${error}`);

            this.authStore.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.customer_users.mfa_mandatory_error"),
                MessageType.ERROR
            );

            return Promise.resolve(false);
        }
    }

    /**
     * Admin only: sets MFA for every user of the caller's customer, optionally only for holders of one
     * portal role (`portalrolle`, e.g. "coowner"). Shows no message — the caller reports the per-user
     * outcome. Resolves undefined when the request failed as a whole.
     */
    async toggleMfaForCustomer(mfaEnabled: boolean, portalrolle?: string): Promise<MfaBulkResult | undefined> {
        info("mfa bulk toggle starts");

        try {
            const response = await Axios.getAuthInstance().post(`${this.config.setMfaForCustomer}`, {
                mfaenabled: mfaEnabled,
                ...(portalrolle ? { portalrolle } : {})
            });
            info("mfa bulk toggle successful");

            return response.data?.data as MfaBulkResult;
        } catch (error) {
            info("mfa bulk toggle not successful");
            info(`--> ${error}`);

            return undefined;
        }
    }

    /**
     * Admin only: sets whether MFA is mandatory for every user of the caller's customer, optionally only
     * for holders of one portal role. Shows no message. Resolves the number of users written, or undefined
     * when the request failed.
     */
    async setMfaMandatoryForCustomer(mandatory: boolean, portalrolle?: string): Promise<number | undefined> {
        info("mfa bulk mandatory starts");

        try {
            const response = await Axios.getAuthInstance().post(`${this.config.setMfaMandatory}`, {
                mandatory,
                ...(portalrolle ? { portalrolle } : {})
            });
            info("mfa bulk mandatory successful");

            return response.data?.data?.updated as number;
        } catch (error) {
            info("mfa bulk mandatory not successful");
            info(`--> ${error}`);

            return undefined;
        }
    }
}

/** The `data` of a toggle-mfa-enabled-customer reply: selected, toggled, and the users that were not. */
export interface MfaBulkResult {
    total: number;
    toggled: number;
    skipped: { userId: string; reason: string }[];
    failed: { userId: string; reason: string }[];
}
